// src/components/auth/ForgotPassword.js
import { confirmResetPassword, resetPassword } from "aws-amplify/auth";
import { useEffect, useState } from "react";
import CodeCountdown from "../../utils/countdown";
import PasswordRequirements from "../../utils/passwordRequirements";
import { isPasswordValid } from "../../utils/passwordValidation";

export default function ForgotPassword({ onBack, onResetSuccess }) {
  const [step, setStep] = useState("request");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendMessage, setResendMessage] = useState(null);
  const [countdownKey, setCountdownKey] = useState(0);

  const passwordValid = isPasswordValid(newPassword);

  useEffect(() => {
    const container = document.querySelector(
      ".greenone-forgot-password"
    );

    if (!container) {
      return undefined;
    }

    function styleSdxButton(button) {
      const shadowRoot = button.shadowRoot;

      if (
        !shadowRoot ||
        shadowRoot.querySelector(
          "[data-greenone-forgot-password-button-styles]"
        )
      ) {
        return;
      }

      const style = document.createElement("style");

      style.setAttribute(
        "data-greenone-forgot-password-button-styles",
        "true"
      );

      style.textContent = `
        button.primary {
          background: #005654 !important;
          border-color: #005654 !important;
          color: #fff !important;
        }

        button.primary:hover {
          background: #026F6D !important;
          border-color: #026F6D !important;
          color: #fff !important;
        }

        button.secondary {
          background: #fff !important;
          border-color: #026F6D !important;
          color: #026F6D !important;
        }

        button.secondary:hover {
          background: #026F6D !important;
          border-color: #026F6D !important;
          color: #fff !important;
        }

        button.primary:focus-visible,
        button.secondary:focus-visible {
          outline: 3px solid #005654 !important;
          outline-offset: 2px !important;
        }
      `;

      shadowRoot.appendChild(style);
    }

    function styleSdxInput(input) {
      const shadowRoot = input.shadowRoot;

      if (
        !shadowRoot ||
        shadowRoot.querySelector(
          "[data-greenone-forgot-password-input-styles]"
        )
      ) {
        return;
      }

      const style = document.createElement("style");

      style.setAttribute(
        "data-greenone-forgot-password-input-styles",
        "true"
      );

      style.textContent = `
        input:focus,
        input:focus-visible {
          border-color: #026F6D !important;
          outline-color: #026F6D !important;
          box-shadow: 0 0 0 1px #026F6D !important;
        }

        :host(:focus-within) input {
          border-color: #026F6D !important;
          outline-color: #026F6D !important;
          box-shadow: 0 0 0 1px #026F6D !important;
        }
      `;

      shadowRoot.appendChild(style);
    }

    function applyComponentStyles() {
      container
        .querySelectorAll("sdx-button")
        .forEach(styleSdxButton);

      container
        .querySelectorAll("sdx-input")
        .forEach(styleSdxInput);
    }

    applyComponentStyles();

    const observer = new MutationObserver(
      applyComponentStyles
    );

    observer.observe(container, {
      childList: true,
      subtree: true,
    });

    return () => {
      observer.disconnect();
    };
  }, []);

  const handleForgotPassword = async () => {
    setError(null);
    setLoading(true);

    try {
      const output = await resetPassword({ username: email });
      const { nextStep } = output;

      if (
        nextStep.resetPasswordStep ===
        "CONFIRM_RESET_PASSWORD_WITH_CODE"
      ) {
        localStorage.removeItem("resetCodeExpiresAt");
        setStep("confirm");
      }
    } catch (err) {
      setError("Fehler: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmReset = async () => {
    setError(null);

    if (!passwordValid) {
      setError("Das Passwort erfüllt nicht alle Anforderungen.");
      return;
    }

    if (newPassword !== confirmPassword) {
      setError("Die Passwörter stimmen nicht überein.");
      return;
    }

    setLoading(true);

    try {
      await confirmResetPassword({
        username: email,
        confirmationCode: code.trim(),
        newPassword,
      });

      if (typeof onResetSuccess === "function") {
        onResetSuccess();
      }
    } catch (err) {
      setError("Fehler: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResendLoading(true);
    setResendMessage(null);
    setError(null);

    try {
      await resetPassword({ username: email });
      localStorage.removeItem("resetCodeExpiresAt");
      setCountdownKey((prev) => prev + 1);
      setResendMessage("Ein neuer Code wurde gesendet.");
    } catch (err) {
      setError("Fehler beim erneuten Senden: " + err.message);
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div
      className="sdx greenone-forgot-password"
      style={{
        position: "relative",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100vh",
        overflow: "hidden",
        padding: "1.5rem",
        boxSizing: "border-box",
      }}
    >
      {/* Hintergrundbild mit Blur */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage: `url('/images/Garage_2.png')`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          filter: "blur(8px) brightness(0.55)",
          transform: "scale(1.05)",
        }}
      ></div>

      {/* Passwort-Reset-Box */}
      <div
        style={{
          position: "relative",
          background: "rgba(255, 255, 255, 0.97)",
          borderRadius: "16px",
          padding: "2rem",
          width: "100%",
          maxWidth: "400px",
          boxShadow: "0 24px 60px rgba(15, 23, 42, 0.28)",
          border: "1px solid #b7cfcc",
          boxSizing: "border-box",
          display: "grid",
          gap: "0.4rem",
        }}
      >
        <h2 style={{ textAlign: "center", marginBottom: "0.5rem", color: "#004A53" }}>
          Passwort zurücksetzen
        </h2>

        {/* Schritt 1: E-Mail eingeben */}
        {step === "request" && (
          <>
            <p style={{ textAlign: "center", color: "#64748b" }}>
              Geben Sie Ihre E-Mail ein, um einen Reset-Code zu erhalten.
            </p>

            <sdx-input
              label="E-Mail"
              placeholder="E-Mail eingeben"
              value={email}
              onInput={(e) => setEmail(e.target.value)}
              clarity-mask="True"
            ></sdx-input>

            {error && (
              <p style={{ color: "red", fontSize: "0.9rem" }}>
                {error}
              </p>
            )}

            <sdx-button
              variant="primary"
              label={loading ? "Wird gesendet..." : "Code senden"}
              onClick={handleForgotPassword}
              disabled={loading}
            ></sdx-button>
          </>
        )}

        {/* Schritt 2: Code und neues Passwort */}
        {step === "confirm" && (
          <>
            <p style={{ textAlign: "center", color: "#64748b" }}>
              Code wurde an <strong>{email}</strong> gesendet.
            </p>

            <CodeCountdown
              key={countdownKey}
              durationInSeconds={3600}
              storageKey="resetCodeExpiresAt"
              onExpire={() => {
                setError(
                  "Der Code ist abgelaufen. Bitte fordern Sie einen neuen Code an."
                );
              }}
            />

            {resendMessage && (
              <p
                style={{
                  textAlign: "center",
                  fontSize: "0.85rem",
                  color: "#64748b",
                }}
              >
                {resendMessage}
              </p>
            )}

            <a
              onClick={!resendLoading ? handleResend : undefined}
              style={{
                textAlign: "center",
                color: resendLoading ? "#999" : "#026F6D",
                cursor: resendLoading ? "default" : "pointer",
                fontSize: "0.9rem",
                display: "block",
              }}
            >
              {resendLoading ? "Senden..." : "Erneut senden"}
            </a>

            <sdx-input
              label="Code"
              placeholder="Code aus E-Mail"
              value={code}
              onInput={(e) => setCode(e.target.value)}
              clarity-mask="True"
            ></sdx-input>

            <sdx-input
              label="Neues Passwort"
              type="password"
              placeholder="Neues Passwort eingeben"
              value={newPassword}
              onInput={(e) => setNewPassword(e.target.value)}
              clarity-mask="True"
            ></sdx-input>

            <sdx-input
              label="Passwort wiederholen"
              type="password"
              placeholder="Neues Passwort bestätigen"
              value={confirmPassword}
              onInput={(e) => setConfirmPassword(e.target.value)}
              clarity-mask="True"
            ></sdx-input>

            {confirmPassword && newPassword !== confirmPassword && (
              <p style={{ color: "red", fontSize: "0.85rem" }}>
                Passwörter stimmen nicht überein.
              </p>
            )}

            <sdx-expand-and-collapse
              expand-label="Passwortkriterien"
              collapse-label="Passwortkriterien"
            >
              <PasswordRequirements password={newPassword} />
            </sdx-expand-and-collapse>

            {error && (
              <p style={{ color: "red", fontSize: "0.9rem" }}>
                {error}
              </p>
            )}

            <sdx-button
              variant="primary"
              label={loading ? "Wird gesetzt..." : "Passwort setzen"}
              onClick={handleConfirmReset}
              disabled={loading}
            ></sdx-button>

            <a
              onClick={() => setStep("request")}
              style={{
                textAlign: "center",
                color: "#026F6D",
                cursor: "pointer",
                fontSize: "0.9rem",
              }}
            >
              Zurück
            </a>
          </>
        )}

        <div style={{ marginTop: "1rem", textAlign: "center" }}>
          <sdx-button
            theme="secondary"
            label="Zurück zum Login"
            onClick={onBack}
          ></sdx-button>
        </div>
      </div>
    </div>
  );
}
